import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useOrderHub } from "../hooks/useOrderHub";
import { ILoginInfo } from "../models/ILoginInfo";
import { getMyRestaurants, IMyRestaurant } from "../services/restaurantUser";
import {
  getActiveRestaurantId, getLinkedRestaurants, rememberLinkedRestaurants, setActiveRestaurantId, useActiveRestaurantId,
} from "../utils/activeRestaurant";
import { getUserToken } from "../utils/Util";

/**
 * Restoran paneli için uygulama genelinde yeni sipariş dinleyicisi.
 *
 * Router'ın içinde ama Routes'un dışında durur: sayfa değişince yeniden kurulmaz, bağlantılar
 * kopmaz. Hesabın bütün restoranlarını dinler; Dashboard açıkken AKTİF restoranı Dashboard'un
 * kendi bağlantısı dinlediği için o burada dinlenmez (zil iki kez çalmasın).
 *
 * Her restorana ayrı bağlantı: NewOrderReceived yükü siparişin restoranını taşımıyor; olay
 * hangi bağlantıdan geldiyse o restoranındır.
 */

interface OrderAlertsContextValue {
  restaurants: IMyRestaurant[];
  /** Restoran id → henüz bakılmamış yeni sipariş sayısı */
  pending: Record<string, number>;
  /** O restorana geç (aynı sekme) ve Dashboard'u aç */
  goToRestaurant: (id: string) => void;
}

const OrderAlertsContext = createContext<OrderAlertsContextValue>({
  restaurants: [],
  pending: {},
  goToRestaurant: () => {},
});

export const useOrderAlerts = () => useContext(OrderAlertsContext);

function RestaurantListener({ restaurantId, onNewOrder }: { restaurantId: string; onNewOrder: (id: string) => void }) {
  useOrderHub({
    token: getUserToken()?.token ?? null,
    restaurantId,
    onNewOrder: () => onNewOrder(restaurantId),
  });
  return null;
}

function playBell() {
  try {
    new Audio(`${process.env.PUBLIC_URL}/ring.mp3`).play()
      .catch((err) => console.warn("[Zil] Ses çalınamadı:", err?.name ?? err));
  } catch {}
}

/** Yalnız restoran paneli oturumları dinler (kurye, canlı destek ve garson panelleri hariç). */
function panelSession(pathname: string): ILoginInfo | null {
  if (pathname === "/" || /login$/i.test(pathname) || pathname.toLowerCase().startsWith("/rentaroom")) return null;
  const s = getUserToken() as ILoginInfo | null;
  if (!s?.token || s.isDriverOnly || s.isLiveChatOnly || s.isWaiterOnly) return null;
  return s;
}

const sameId = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();

export default function OrderAlertsProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  useActiveRestaurantId(); // aynı sekmede seçim değişince yeniden render
  const activeId = getActiveRestaurantId(); // giriş/çıkış sonrası da güncel (konum değişince render olur)
  const session = panelSession(location.pathname);
  const userId = session?.userId ?? "";
  const onDashboard = location.pathname.toLowerCase().startsWith("/dashboard");
  const hasSession = !!session;

  const [restaurants, setRestaurants] = useState<IMyRestaurant[]>([]);
  const [pending, setPending] = useState<Record<string, number>>({});
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const titleTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const originalTitle = useRef(document.title);

  // Oturum (kullanıcı) değişince restoran listesi baştan okunur.
  useEffect(() => {
    setPending({});
    setDismissed(new Set());
    if (!userId) { setRestaurants([]); return; }
    let cancelled = false;
    const fromLogin = getLinkedRestaurants();
    if (fromLogin.length > 0) setRestaurants(fromLogin.map((r) => ({ ...r, isOpenNow: false })));
    getMyRestaurants()
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res.data) ? res.data : [];
        rememberLinkedRestaurants(list);
        setRestaurants(list);
      })
      .catch(() => { /* uç yoksa/yetki yoksa aşağıdaki tek restoran yedeği kullanılır */ });
    return () => { cancelled = true; };
  }, [userId]);

  // Liste alınamadıysa (eski backend, tek restoranlı hesap) en azından aktif restoran dinlenir.
  const effective = useMemo<IMyRestaurant[]>(() => {
    if (!hasSession) return [];
    if (restaurants.length > 0) return restaurants;
    return activeId ? [{ id: activeId, name: "", isOpenNow: false }] : [];
  }, [hasSession, restaurants, activeId]);

  const nameOf = useCallback(
    (id: string) => effective.find((r) => sameId(r.id, id))?.name ?? "",
    [effective]
  );

  const stopTitleFlash = () => {
    if (!titleTimer.current) return;
    clearInterval(titleTimer.current);
    titleTimer.current = null;
    document.title = originalTitle.current;
  };

  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default" && hasSession) {
      Notification.requestPermission().catch(() => {});
    }
    const onVisible = () => { if (document.visibilityState === "visible") stopTitleFlash(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSession]);

  const handleNewOrder = useCallback((id: string) => {
    setPending((prev) => ({ ...prev, [id]: (prev[id] ?? 0) + 1 }));
    setDismissed((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev); next.delete(id); return next;
    });
    playBell();
    const name = nameOf(id);
    if ("Notification" in window && Notification.permission === "granted") {
      new Notification(name ? `Yeni sipariş: ${name}` : "Yeni Sipariş!", {
        body: "Yeni bir sipariş geldi, lütfen kontrol edin.",
        icon: "/logo192.png",
      });
    }
    if (document.visibilityState !== "visible" && !titleTimer.current) {
      let on = false;
      titleTimer.current = setInterval(() => {
        document.title = on ? originalTitle.current : `🔴 ${name ? `${name}: ` : ""}Yeni Sipariş!`;
        on = !on;
      }, 1000);
    }
  }, [nameOf]);

  const goToRestaurant = useCallback((id: string) => {
    setPending((prev) => { const next = { ...prev }; delete next[id]; return next; });
    if (!sameId(id, getActiveRestaurantId())) setActiveRestaurantId(id);
    if (!location.pathname.toLowerCase().startsWith("/dashboard")) navigate("/dashboard");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [location.pathname, navigate]);

  // Dashboard'da aktif restorana bakılıyorsa onun bekleyen uyarısı görülmüş sayılır.
  useEffect(() => {
    if (!onDashboard || !activeId) return;
    setPending((prev) => {
      const key = Object.keys(prev).find((k) => sameId(k, activeId));
      if (!key) return prev;
      const next = { ...prev }; delete next[key]; return next;
    });
  }, [onDashboard, activeId]);

  const listened = effective.filter((r) => !(onDashboard && sameId(r.id, activeId)));
  const alerts = effective.filter((r) => (pending[r.id] ?? 0) > 0 && !dismissed.has(r.id)
    && !(onDashboard && sameId(r.id, activeId)));

  const value = useMemo(() => ({ restaurants, pending, goToRestaurant }), [restaurants, pending, goToRestaurant]);

  return (
    <OrderAlertsContext.Provider value={value}>
      {listened.map((r) => (
        <RestaurantListener key={r.id} restaurantId={r.id} onNewOrder={handleNewOrder} />
      ))}

      {alerts.length > 0 && (
        <div className="mr-alerts" role="alert" aria-live="assertive">
          {alerts.map((r) => {
            const count = pending[r.id] ?? 0;
            const isActive = sameId(r.id, activeId);
            return (
              <div key={r.id} className="mr-alert">
                <button type="button" className="mr-alert__go" onClick={() => goToRestaurant(r.id)}>
                  <i className="bi bi-bell-fill mr-alert__bell"></i>
                  <span className="mr-alert__text">
                    {r.name && !isActive ? <><strong>{r.name}</strong> restoranına </> : null}
                    {count > 1 ? `${count} yeni sipariş` : "Yeni sipariş"} geldi
                  </span>
                  <span className="mr-alert__cta">Siparişe git <i className="bi bi-arrow-right"></i></span>
                </button>
                <button type="button" className="mr-alert__close" aria-label="Uyarıyı kapat"
                  onClick={() => setDismissed((prev) => new Set(prev).add(r.id))}>
                  <i className="bi bi-x-lg"></i>
                </button>
              </div>
            );
          })}
        </div>
      )}

      {children}
    </OrderAlertsContext.Provider>
  );
}
